/**
 * BSG — UX & Conversion Enhancements (v2 — Performance Optimised)
 *
 * What changed vs v1:
 *   - Back-to-top & WhatsApp elements are now SERVER-RENDERED in theme.liquid.
 *     This file only handles scroll-visibility toggling (no DOM creation = no CLS).
 *   - Everything non-critical runs via requestIdleCallback.
 *   - Urgency text uses a page-stable pseudo-random count (consistent across
 *     renders of the same product URL — avoids "fake live" CLS on re-renders).
 */

'use strict';

/* ── 1. Back-to-top scroll visibility ──────────────────────────── */
function initBackToTop() {
  const btn = document.getElementById('bsg-back-to-top');
  if (!btn) return;

  // BSG FIX (desktop not showing): at >=990px this theme runs a "squeeze"
  // layout where `.page-wrapper` — not `window`/`document` — is the real
  // scrolling element (see assets/scroll-container.js). `html`/`body` get
  // `overflow: hidden` at that breakpoint, so `window.scrollY` never
  // changes and a `window` "scroll" listener never fires there. That's why
  // the button worked on mobile (where the document root scrolls natively)
  // but never appeared on desktop. We mirror the theme's own scroll
  // container detection instead of assuming `window` always scrolls.
  const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

  function getScrollContainer() {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function getScrollEventTarget() {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') || document;
    }
    return document;
  }

  let ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(function () {
        const isVisible = getScrollContainer().scrollTop > 400;
        btn.classList.toggle('is-visible', isVisible);
        btn.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        btn.setAttribute('tabindex', isVisible ? '0' : '-1');
        ticking = false;
      });
      ticking = true;
    }
  }

  let currentTarget = null;
  function bindScrollListener() {
    const target = getScrollEventTarget();
    if (target === currentTarget) return;
    if (currentTarget) currentTarget.removeEventListener('scroll', onScroll);
    currentTarget = target;
    currentTarget.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // re-evaluate immediately (covers breakpoint changes mid-session)
  }

  bindScrollListener();
  // The scroll container swaps when crossing the 990px breakpoint
  // (e.g. rotating a tablet) — rebind so visibility tracking keeps working.
  SQUEEZE_QUERY.addEventListener('change', bindScrollListener);

  btn.addEventListener('click', function () {
    getScrollContainer().scrollTo({ top: 0, behavior: 'smooth' });
    // Move focus to top landmark for accessibility
    const skipLink = document.querySelector('#MainContent');
    if (skipLink) skipLink.focus({ preventScroll: true });
  });
}

/* ── 2. Urgency live-viewer text (product pages only) ──────────── */
function initUrgencyText() {
  const main = document.querySelector('main[data-template="product"]');
  if (!main) return;

  const atcContainer = document.querySelector('add-to-cart-component');
  if (!atcContainer) return;

  // Stable count derived from product URL — same across re-renders,
  // looks realistic, avoids the "jumpiness" of Math.random().
  const href    = window.location.pathname;
  let   hashSum = 0;
  for (let i = 0; i < href.length; i++) hashSum += href.charCodeAt(i);
  const viewers = (hashSum % 12) + 5; // 5–16

  const div           = document.createElement('div');
  div.className       = 'bsg-urgency-text';
  div.setAttribute('aria-live', 'polite');
  div.setAttribute('role', 'status');
  div.innerHTML       =
    '<span class="bsg-urgency-text__dot" aria-hidden="true"></span>' +
    '<span>' + viewers + ' people are viewing this right now</span>';

  atcContainer.parentNode.insertBefore(div, atcContainer.nextSibling);
}

/* ── 3. Lazy image fade-in (IntersectionObserver) ──────────────── */
function initLazyFade() {
  if (!('IntersectionObserver' in window)) return;

  const images = document.querySelectorAll('img[loading="lazy"]');
  if (!images.length) return;

  const observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      img.style.transition = 'opacity 0.3s ease';
      // If already decoded, just ensure visible
      if (img.complete) {
        img.style.opacity = '1';
      } else {
        img.style.opacity = '0';
        img.addEventListener('load', function () {
          img.style.opacity = '1';
        }, { once: true });
      }
      observer.unobserve(img);
    });
  }, { rootMargin: '0px 0px 200px 0px' });

  images.forEach(function (img) { observer.observe(img); });
}

/* ── Boot ───────────────────────────────────────────────────────── */
function boot() {
  // Critical: scroll-visibility toggling (lightweight, needed immediately)
  initBackToTop();

  // BSG FIX: initUrgencyText() inserts a new DOM node next to the Add to
  // Cart button. Running it via requestIdleCallback (previously up to 2s
  // after load) meant it could pop in *after* the page had visually
  // settled and the shopper had already started reading/clicking around
  // the buy box — a late, jarring layout shift that read as the product
  // page "not loading smooth". It's a synchronous, non-network operation
  // (just a string hash + one DOM insert), so there's no performance
  // reason to defer it. Running it immediately means the shift (if any)
  // happens before first paint settles, not mid-interaction.
  initUrgencyText();

  // Still non-critical: run when browser is idle
  var idleFn = window.requestIdleCallback
    ? function (fn, opts) { requestIdleCallback(fn, opts); }
    : function (fn)       { setTimeout(fn, 300); };

  idleFn(initLazyFade, { timeout: 3000 });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
